/**
 * 文件定位：
 * 1. 当前文件位于 Worker 倒角求解纯函数层；
 * 2. 负责顶点倒角和 Face Face 倒角共享的轻量向量计算；
 * 3. 不依赖 Puppy CAD 内核，保持函数式、可测试。
 *
 * 文件职责：
 * 1. 提供三维点和向量的基础运算；
 * 2. 集中维护倒角距离容差；
 * 3. 避免 solver class 内散落重复数学逻辑。
 */

/**
 * 边距离容差。
 */
export const EDGE_DISTANCE_EPS = 1e-6;

/**
 * Face Face 帮助点匹配容差。
 */
export const FACE_FACE_HELP_POINT_EPS = 1e-6;

/**
 * 三维点或三维向量。
 */
export type Vector3Tuple = [number, number, number];

/**
 * 向量相减。
 *
 * @param left 左侧向量。
 * @param right 右侧向量。
 * @returns 相减后的向量。
 */
export function vectorSubtract(
  left: Vector3Tuple,
  right: Vector3Tuple,
): Vector3Tuple {
  return [left[0] - right[0], left[1] - right[1], left[2] - right[2]];
}

/**
 * 计算向量长度。
 *
 * @param vector 输入向量。
 * @returns 向量长度。
 */
export function vectorLength(vector: Vector3Tuple): number {
  return Math.sqrt(
    vector[0] * vector[0]
    + vector[1] * vector[1]
    + vector[2] * vector[2],
  );
}

/**
 * 归一化向量。
 *
 * @param vector 输入向量。
 * @returns 单位向量。
 */
export function normalizeVector(vector: Vector3Tuple): Vector3Tuple {
  const length = vectorLength(vector);
  if (length <= EDGE_DISTANCE_EPS) {
    throw new Error('倒角方向退化，无法归一化向量');
  }
  return [vector[0] / length, vector[1] / length, vector[2] / length];
}

/**
 * 计算叉乘。
 *
 * @param left 左侧向量。
 * @param right 右侧向量。
 * @returns 叉乘向量。
 */
export function cross(
  left: Vector3Tuple,
  right: Vector3Tuple,
): Vector3Tuple {
  return [
    left[1] * right[2] - left[2] * right[1],
    left[2] * right[0] - left[0] * right[2],
    left[0] * right[1] - left[1] * right[0],
  ];
}

/**
 * 计算两点距离。
 *
 * @param left 左侧点。
 * @param right 右侧点。
 * @returns 距离。
 */
export function measurePointDistance(
  left: Vector3Tuple,
  right: Vector3Tuple,
): number {
  return vectorLength(vectorSubtract(left, right));
}

/**
 * 判断两个点是否在容差内重合。
 *
 * @param left 左侧点。
 * @param right 右侧点。
 * @param tolerance 容差。
 * @returns 是否近似重合。
 */
export function arePointsNear(
  left: Vector3Tuple,
  right: Vector3Tuple,
  tolerance: number = FACE_FACE_HELP_POINT_EPS,
): boolean {
  return Math.abs(left[0] - right[0]) <= tolerance
    && Math.abs(left[1] - right[1]) <= tolerance
    && Math.abs(left[2] - right[2]) <= tolerance;
}

/**
 * 计算边端点中远离顶点的一端。
 *
 * @param edgeVertices 边端点。
 * @param vertexPoint 顶点坐标。
 * @returns 远端坐标。
 */
export function chooseEdgeFarPoint(
  edgeVertices: Vector3Tuple[],
  vertexPoint: Vector3Tuple,
): Vector3Tuple {
  const [firstPoint, secondPoint] = edgeVertices;
  if (!firstPoint || !secondPoint) {
    throw new Error('顶点倒角缺少有效边端点');
  }
  const firstDistance = vectorLength(vectorSubtract(firstPoint, vertexPoint));
  const secondDistance = vectorLength(vectorSubtract(secondPoint, vertexPoint));
  return firstDistance > secondDistance ? firstPoint : secondPoint;
}

/**
 * 计算边中点。
 *
 * @param vertices 边端点。
 * @returns 中点；端点不足时返回 null。
 */
export function computeEdgeMidpoint(
  vertices: Vector3Tuple[],
): Vector3Tuple | null {
  const [start, end] = vertices;
  if (!start || !end) {
    return null;
  }
  return [
    (start[0] + end[0]) / 2,
    (start[1] + end[1]) / 2,
    (start[2] + end[2]) / 2,
  ];
}

